import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Sparkles, Wand2, FileText, Hash, Type, Megaphone, Loader2, AlertCircle, GraduationCap } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import api from '@/lib/api';
import { useT } from '@/lib/i18n';
import AIAnalysisCard from './AIAnalysisCard';
import WhyThisClipCard from './WhyThisClipCard';
import GeneratedContentCard from './GeneratedContentCard';
import HashtagList from './HashtagList';
import TitleSuggestions from './TitleSuggestions';
import CTAList from './CTAList';
import CoachPanel from './CoachPanel';

const TABS = [
  { key: 'coach', icon: GraduationCap, labelFr: 'Coach', labelEn: 'Coach' },
  { key: 'analysis', icon: Sparkles, labelFr: 'Analyse', labelEn: 'Analysis' },
  { key: 'content', icon: FileText, labelFr: 'Contenu', labelEn: 'Content' },
  { key: 'hashtags', icon: Hash, labelFr: 'Hashtags', labelEn: 'Hashtags' },
  { key: 'titles', icon: Type, labelFr: 'Titres', labelEn: 'Titles' },
  { key: 'ctas', icon: Megaphone, labelFr: 'CTAs', labelEn: 'CTAs' },
];

const AIAssistantPanel = ({ clip }) => {
  const { lang } = useT();
  const [tab, setTab] = useState('coach');
  const [insights, setInsights] = useState(null);
  const [insightsLoading, setInsightsLoading] = useState(true);
  const [content, setContent] = useState(null);
  const [contentLoading, setContentLoading] = useState(false);
  const [contentError, setContentError] = useState(null);
  const [coach, setCoach] = useState(null);
  const [coachLoading, setCoachLoading] = useState(true);
  const [coachGenerating, setCoachGenerating] = useState(false);
  const [coachError, setCoachError] = useState(null);

  // Load deterministic insights (no LLM cost) immediately
  useEffect(() => {
    if (!clip?.id) return;
    let cancelled = false;
    setInsightsLoading(true);
    api.get(`/clips/${clip.id}/ai-insights`).then((r) => {
      if (!cancelled) setInsights(r.data);
    }).catch(() => {
      if (!cancelled) toast.error(lang === 'fr' ? 'Analyse IA indisponible' : 'AI analysis unavailable');
    }).finally(() => { if (!cancelled) setInsightsLoading(false); });
    return () => { cancelled = true; };
  }, [clip?.id, lang]);

  // Load coach payload (fresh scores + cached recs/strengths) immediately.
  // If the backend reports status="generating" (long-running GPT job in the
  // background), poll every 3s until it becomes "ready".
  useEffect(() => {
    if (!clip?.id) return;
    let cancelled = false;
    let pollTimer = null;

    const fetchOnce = async () => {
      try {
        const { data } = await api.get(`/clips/${clip.id}/ai-coach`);
        if (cancelled) return;
        setCoach(data);
        setCoachError(null);
        if (data.status === 'generating') {
          setCoachGenerating(true);
          pollTimer = setTimeout(fetchOnce, 3000);
        } else {
          setCoachGenerating(false);
        }
      } catch (e) {
        if (cancelled) return;
        setCoachError(e?.response?.data?.detail || (lang === 'fr' ? 'Coach IA indisponible' : 'AI coach unavailable'));
        setCoachGenerating(false);
      } finally {
        if (!cancelled) setCoachLoading(false);
      }
    };

    setCoachLoading(true);
    setCoachError(null);
    fetchOnce();
    return () => {
      cancelled = true;
      if (pollTimer) clearTimeout(pollTimer);
    };
  }, [clip?.id, lang]);

  const generateCoach = async () => {
    setCoachGenerating(true);
    setCoachError(null);
    try {
      // POST returns 202 with {status:"generating"} — actual coaching arrives via polling.
      await api.post(`/clips/${clip.id}/ai-coach`);
      toast.success(lang === 'fr' ? 'Génération lancée…' : 'Generation started…');
      // Kick off a fresh poll cycle immediately.
      let attempts = 0;
      const maxAttempts = 60; // 60 × 3s = 3 min ceiling
      const poll = async () => {
        try {
          const { data } = await api.get(`/clips/${clip.id}/ai-coach`);
          setCoach(data);
          if (data.status === 'ready') {
            setCoachGenerating(false);
            toast.success(lang === 'fr' ? 'Conseils générés' : 'Coaching generated');
            return;
          }
          attempts += 1;
          if (attempts >= maxAttempts) {
            setCoachGenerating(false);
            setCoachError(lang === 'fr' ? 'Génération trop longue, réessaie' : 'Generation timed out, retry');
            return;
          }
          setTimeout(poll, 3000);
        } catch (e) {
          setCoachGenerating(false);
          setCoachError(e?.response?.data?.detail || (lang === 'fr' ? 'Erreur du coach IA' : 'AI coach error'));
        }
      };
      setTimeout(poll, 2000);
    } catch (e) {
      const msg = e?.response?.data?.detail || (lang === 'fr' ? 'Erreur du coach IA' : 'AI coach error');
      setCoachError(msg);
      setCoachGenerating(false);
      toast.error(msg);
    }
  };

  // Try to fetch cached content; if 404, leave it null (user must click "Generate")
  useEffect(() => {
    if (!clip?.id) return;
    let cancelled = false;
    api.get(`/clips/${clip.id}/ai-content`).then((r) => {
      if (!cancelled) setContent(r.data);
    }).catch(() => { /* cached miss → fine */ });
    return () => { cancelled = true; };
  }, [clip?.id]);

  const generateContent = async () => {
    setContentLoading(true);
    setContentError(null);
    try {
      const { data } = await api.post(`/clips/${clip.id}/ai-content`);
      setContent(data);
      toast.success(lang === 'fr' ? 'Contenu généré' : 'Content generated');
    } catch (e) {
      const msg = e?.response?.data?.detail || (lang === 'fr' ? 'Erreur de génération IA' : 'AI generation error');
      setContentError(msg);
      toast.error(msg);
    } finally {
      setContentLoading(false);
    }
  };

  const updateContentField = (key) => (val) => setContent((c) => ({ ...c, [key]: val }));

  // Local-only field updaters (saved client-side; backend cache stays the last AI generation)
  const fields = [
    { key: 'tiktok_caption', titleFr: 'TikTok Caption', titleEn: 'TikTok Caption', icon: FileText },
    { key: 'instagram_caption', titleFr: 'Instagram Caption', titleEn: 'Instagram Caption', icon: FileText },
    { key: 'linkedin_post', titleFr: 'Post LinkedIn', titleEn: 'LinkedIn Post', icon: FileText },
    { key: 'x_post', titleFr: 'Post X', titleEn: 'X Post', icon: FileText },
    { key: 'youtube_title', titleFr: 'Titre YouTube', titleEn: 'YouTube Title', icon: Type },
    { key: 'youtube_description', titleFr: 'Description YouTube', titleEn: 'YouTube Description', icon: FileText },
  ];

  const needsGeneration = !content && tab !== 'analysis' && tab !== 'coach';

  return (
    <motion.section
      initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}
      className="mt-10"
      data-testid="ai-assistant-panel"
    >
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-[#D4FF00]/10 border border-[#D4FF00]/30 flex items-center justify-center">
            <Wand2 className="w-4 h-4 text-[#D4FF00]" />
          </div>
          <div>
            <h2 className="font-heading font-bold text-lg sm:text-xl">
              {lang === 'fr' ? 'Assistant IA' : 'AI Assistant'}
            </h2>
            <p className="text-xs text-zinc-500">
              {lang === 'fr' ? 'Scores, raisons et contenu prêt à publier' : 'Scores, reasons and ready-to-post content'}
            </p>
          </div>
        </div>
        {tab !== 'analysis' && tab !== 'coach' && (
          <Button
            onClick={generateContent}
            disabled={contentLoading}
            className="bg-[#D4FF00] text-black hover:bg-[#D4FF00]/90 font-semibold h-9 px-4 text-xs sm:text-sm"
            data-testid="ai-content-regenerate-all"
          >
            {contentLoading ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5 mr-1.5" />}
            {content ? (lang === 'fr' ? 'Re-générer tout' : 'Regenerate all') : (lang === 'fr' ? 'Générer le contenu' : 'Generate content')}
          </Button>
        )}
      </div>

      {/* Tab bar */}
      <div className="flex items-center gap-1 mb-5 border-b border-white/10 overflow-x-auto" role="tablist">
        {TABS.map((t) => {
          const active = tab === t.key;
          const Icon = t.icon;
          return (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`flex items-center gap-1.5 px-4 py-2.5 text-xs sm:text-sm whitespace-nowrap transition-colors border-b-2 ${
                active ? 'border-[#D4FF00] text-white' : 'border-transparent text-zinc-500 hover:text-zinc-300'
              }`}
              data-testid={`ai-tab-${t.key}`}
              role="tab"
              aria-selected={active}
            >
              <Icon className={`w-3.5 h-3.5 ${active ? 'text-[#D4FF00]' : ''}`} />
              {lang === 'fr' ? t.labelFr : t.labelEn}
            </button>
          );
        })}
      </div>

      {/* Tab content */}
      {tab === 'analysis' && (
        <div className="space-y-4">
          <AIAnalysisCard insights={insights} loading={insightsLoading} />
          <WhyThisClipCard insights={insights} loading={insightsLoading} />
        </div>
      )}

      {tab !== 'analysis' && needsGeneration && !contentLoading && (
        <div className="rounded-2xl border border-dashed border-white/10 bg-[#0a0a0a]/50 p-10 text-center" data-testid="ai-content-empty-state">
          {contentError ? (
            <div className="flex flex-col items-center gap-3">
              <AlertCircle className="w-6 h-6 text-red-400" />
              <p className="text-sm text-red-400">{contentError}</p>
              <Button onClick={generateContent} variant="outline" className="border-white/10 text-zinc-300">{lang === 'fr' ? 'Réessayer' : 'Retry'}</Button>
            </div>
          ) : (
            <>
              <Wand2 className="w-8 h-8 mx-auto text-[#D4FF00] mb-3" />
              <p className="text-zinc-300 text-sm mb-1 font-semibold">{lang === 'fr' ? 'Génère le contenu social en un clic' : 'Generate social content in one click'}</p>
              <p className="text-zinc-500 text-xs mb-5">{lang === 'fr' ? 'Captions, hashtags, titres et CTAs à partir du transcript de ton clip.' : 'Captions, hashtags, titles and CTAs from your clip transcript.'}</p>
              <Button onClick={generateContent} className="bg-[#D4FF00] text-black hover:bg-[#D4FF00]/90 font-semibold">
                <Wand2 className="w-4 h-4 mr-2" />
                {lang === 'fr' ? 'Générer maintenant' : 'Generate now'}
              </Button>
            </>
          )}
        </div>
      )}

      {tab !== 'analysis' && tab !== 'coach' && contentLoading && !content && (
        <div className="rounded-2xl border border-white/10 bg-[#0a0a0a] p-10 text-center" data-testid="ai-content-loading">
          <Loader2 className="w-6 h-6 mx-auto text-[#D4FF00] animate-spin mb-3" />
          <p className="text-zinc-400 text-sm">{lang === 'fr' ? 'GPT-5.2 rédige tes posts…' : 'GPT-5.2 is writing your posts…'}</p>
        </div>
      )}

      {tab === 'coach' && (
        <CoachPanel
          coach={coach}
          loading={coachLoading}
          generating={coachGenerating}
          error={coachError}
          onGenerate={generateCoach}
          lang={lang}
        />
      )}

      {tab === 'content' && content && (
        <div className="grid sm:grid-cols-2 gap-3">
          {fields.map((f) => (
            <GeneratedContentCard
              key={f.key}
              title={lang === 'fr' ? f.titleFr : f.titleEn}
              icon={f.icon}
              text={content[f.key] || ''}
              onChange={updateContentField(f.key)}
              onRegenerate={generateContent}
              testid={`content-${f.key}`}
            />
          ))}
        </div>
      )}

      {tab === 'hashtags' && content && (
        <HashtagList hashtags={content.hashtags || []} />
      )}

      {tab === 'titles' && content && (
        <TitleSuggestions titles={content.titles || []} />
      )}

      {tab === 'ctas' && content && (
        <CTAList ctas={content.ctas || []} />
      )}
    </motion.section>
  );
};

export default AIAssistantPanel;
